import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildMetaUserData } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { MetaPixelAdapter, META_PIXEL_ADAPTER_KEY, META_PIXEL_SCRIPT };

const META_PIXEL_ADAPTER_KEY = "meta_pixel";
const META_PIXEL_SCRIPT = "https://connect.facebook.net/en_US/fbevents.js";

/**
 * Adaptador del píxel de Meta (`fbq`).
 *
 * Particularidades de la red que se resuelven aquí (el resto viaja hecho en
 * el manifest):
 *
 * - **Instancia compartida**: si `window.fbq` ya existe (GTM cargó el píxel
 *   antes) NO se redefine, igual que hace el snippet oficial. Los eventos se
 *   encolan y se procesan cuando `fbevents.js` termina de cargar.
 * - **Siempre `trackSingle`/`trackSingleCustom`**: en la misma página puede
 *   haber píxeles ajenos inicializados por una agencia vía GTM; `track` a
 *   secas haría broadcast a todos y contaminaría cuentas que no son nuestras.
 * - **`PageView` no es automático**: lo dispara el mapping `page_view` del
 *   manifest, como cualquier otro evento.
 * - **`eventID`** (camelCase, solo en Meta) es el gancho de la dedup con el
 *   CAPI: mismo nombre de evento + mismo id + mismo píxel en 48 h = un evento.
 */
class MetaPixelAdapter implements BrowserPixelAdapter {
  readonly key = META_PIXEL_ADAPTER_KEY;
  private _loaded = false;
  private _initialized: Record<string, boolean> = {};
  private _onScriptError: ScriptErrorReporter | null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      if (window.fbq) {
        // Ya cargado por GTM o por el tenant: instancia compartida.
        return;
      }

      // Stub oficial: cola de llamadas hasta que fbevents.js define
      // callMethod. Se mantiene `arguments` (y no rest args) porque es lo que
      // el vendor espera encontrar en la cola.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fbq: any = function () {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        if (fbq.callMethod) {
          // eslint-disable-next-line prefer-spread
          fbq.callMethod.apply(fbq, args);
        } else {
          fbq.queue.push(args);
        }
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      window.fbq = fbq;
      if (!window._fbq) {
        window._fbq = fbq;
      }

      injectVendorScript(META_PIXEL_SCRIPT, this.key, this._onScriptError);
    } catch (e) {
      // sin píxel de Meta la página sigue funcionando igual
    }
  }

  /**
   * Un `init` por pixel_id. Meta emite un warning "Duplicate Pixel ID" al
   * repetirlo y NO está verificado que un segundo init actualice el advanced
   * matching de un píxel ya inicializado, así que aquí se ignora: la cobertura
   * del email del formulario viene del Automatic Advanced Matching de Events
   * Manager y del CAPI, que sí lo manda hasheado.
   */
  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!pixelId || this._initialized[pixelId]) {
        return;
      }
      const fbq = this._fbq();
      if (!fbq) {
        return;
      }
      this._initialized[pixelId] = true;
      const userData = buildMetaUserData(identity);
      if (Object.keys(userData).length > 0) {
        fbq("init", pixelId, userData);
      } else {
        fbq("init", pixelId);
      }
    } catch (e) {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      const fbq = this._fbq();
      if (!fbq || !pixelId || !event?.name) {
        return;
      }
      const method = event.custom ? "trackSingleCustom" : "trackSingle";
      fbq(method, pixelId, event.name, event.params ?? {}, {
        eventID: eventId,
      });
    } catch (e) {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      this._fbq()?.("consent", granted ? "grant" : "revoke");
    } catch (e) {
      // sin consentimiento aplicado, el píxel se queda como estaba
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _fbq(): any {
    return typeof window.fbq === "function" ? window.fbq : null;
  }
}
